/**
 * BarometerSection - section on call detail page showing deficit professions.
 *
 * Displays all districts' barometer data and optional voivodeship-level data.
 * Renders only when data is available or loading.
 *
 * @see docs/specifications/2026-03-06-kfs-barometr-zawodow.md
 */
import React, { useState } from "react";
import { BarometerResponse } from "../../../../types/grants";

interface BarometerSectionProps {
  /** Barometer response data (undefined when not loaded or on error) */
  barometerData: BarometerResponse | undefined;
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Whether fetching resulted in an error */
  isError?: boolean;
}

/**
 * Renders a profession list with colored border indicator.
 */
const ProfessionList: React.FC<{
  title: string;
  professions: string[];
  variant: "large" | "normal";
}> = ({ title, professions, variant }) => {
  if (professions.length === 0) return null;

  const bgColor = variant === "large" ? "bg-red-50" : "bg-orange-50";
  const borderColor = variant === "large" ? "border-red-400" : "border-orange-400";
  const titleColor = variant === "large" ? "text-red-700" : "text-orange-700";
  const dotColor = variant === "large" ? "bg-red-600" : "bg-orange-500";

  return (
    <div className="mb-3">
      <h4 className={`text-xs font-semibold ${titleColor} mb-1 flex items-center`}>
        <span className={`inline-block w-2 h-2 ${dotColor} rounded-full mr-2`} />
        {title} ({professions.length})
      </h4>
      <ul className="space-y-1">
        {professions.map((profession, index) => (
          <li
            key={index}
            className={`${bgColor} border-l-3 ${borderColor} px-2 py-1 text-sm text-left`}
          >
            {profession}
          </li>
        ))}
      </ul>
    </div>
  );
};

const BarometerSection: React.FC<BarometerSectionProps> = ({
  barometerData,
  isLoading,
  isError = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const totalDeficitCount = barometerData
    ? barometerData.districts.reduce(
        (sum, d) => sum + d.largeShortageProfessions.length + d.shortageProfessions.length,
        0
      )
    : 0;

  const headerTitle = barometerData
    ? `Barometr zawodów ${barometerData.year}`
    : "Barometr zawodów";

  // Loading state: show heading + skeleton
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <h2 className="text-lg font-medium text-neutral-900 mb-4 text-left">
          Barometr zawodów
        </h2>
        <div className="space-y-3">
          <div
            data-testid="barometer-section-skeleton"
            className="animate-pulse"
            role="status"
            aria-busy="true"
            aria-label="Ładowanie barometru..."
          >
            <div className="h-4 bg-neutral-200 rounded w-48 mb-2" />
            <div className="h-3 bg-neutral-200 rounded w-full mb-1" />
            <div className="h-3 bg-neutral-200 rounded w-3/4 mb-1" />
            <div className="h-3 bg-neutral-200 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <h2 className="text-lg font-medium text-neutral-900 mb-4 text-left">
          Barometr zawodów
        </h2>
        <p className="text-sm text-neutral-500">
          Nie udało się załadować danych barometru zawodów.
        </p>
      </div>
    );
  }

  // No data or empty districts: hide
  if (!barometerData || barometerData.districts.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-neutral-50 transition-colors"
        aria-expanded={isExpanded}
      >
        <div>
          <h2 className="text-lg font-medium text-neutral-900">
            {headerTitle}
          </h2>
          <p className="text-sm text-neutral-500">
            {totalDeficitCount} zawodów z deficytem
          </p>
        </div>
        <svg
          className={`h-5 w-5 text-neutral-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-neutral-200 pt-4">
          {/* Per-district data */}
          {barometerData.districts.map((district) => (
            <div key={district.code} className="mb-6">
              <h3 className="text-sm font-semibold text-neutral-800 mb-2 text-left">
                {district.name}
              </h3>

              <ProfessionList
                title="Duży deficyt"
                professions={district.largeShortageProfessions}
                variant="large"
              />
              <ProfessionList
                title="Deficyt"
                professions={district.shortageProfessions}
                variant="normal"
              />

              {district.largeShortageProfessions.length === 0 &&
                district.shortageProfessions.length === 0 && (
                  <p className="text-sm text-neutral-500">
                    Brak zawodów z deficytem w tym powiecie.
                  </p>
                )}
            </div>
          ))}

          {/* Voivodeship data */}
          {barometerData.voivodeship && (
            <div className="mt-4 pt-4 border-t border-neutral-200">
              <h3 className="text-sm font-semibold text-neutral-800 mb-2 text-left">
                {"Dane województwa: "}{barometerData.voivodeship.name}
              </h3>
              <ProfessionList
                title="Duży deficyt"
                professions={barometerData.voivodeship.largeShortageProfessions}
                variant="large"
              />
              <ProfessionList
                title="Deficyt"
                professions={barometerData.voivodeship.shortageProfessions}
                variant="normal"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BarometerSection;
